import type React from 'react';
import { useMemo } from 'react';
import { EditorContent } from '@tiptap/react';
import { useQuery } from '@tanstack/react-query';

import { useEmailTemplateEditor } from './hooks/useEmailTemplateEditor';
import FormattingToolbar from './Toolbar';
import DefaultFontLoader from 'components/font/DefaultFontLoader';
import { DOWNLOADED_FONTS } from 'components/editor/grid/reduxStore/defaultTextDefaultValue';
import { EmailTemplateRichTextEditorLayout } from './EmailTemplateRichTextEditorLayout';
import { emailTemplatesQueries } from '../../hooks/emailTemplatesQueries';
import type { EmailTemplate, EmailTemplateEventType } from '../../types';
import type { EmailTemplateToolbarOption } from './components/EmailTemplatePickerButton';
import { FeatureFlags } from 'utils/featureFlags';
import useFeatureFlag from 'hooks/useFeatureFlag';

function toEmailTemplateToolbarOptions(
  templates: EmailTemplate[],
  emailTemplatesEventTypesAllowed: EmailTemplateEventType[]
): EmailTemplateToolbarOption[] {
  return templates
    .filter((emailTemplate) => emailTemplatesEventTypesAllowed.includes(emailTemplate.eventType))
    .map((emailTemplate) => ({
      id: `${emailTemplate.scope}/${emailTemplate.eventType}/${emailTemplate.versionSegment}`,
      name: emailTemplate.name,
      html: emailTemplate.html,
      subject: emailTemplate.subject,
      templateId: emailTemplate.id,
      eventType: emailTemplate.eventType,
    }));
}

export interface EmailTemplateRichTextEditorProps {
  editorClassName?: string;
  errorMessage?: string;
  headerLabel?: React.ReactNode;
  placeholder?: string;
  readOnly?: boolean;
  showEmailTemplateOptions?: boolean;
  emailTemplatesEventTypesAllowed?: EmailTemplateEventType[];
  value?: string;
  onChange: (html: string) => void;
  onTemplateSelect?: (option: EmailTemplateToolbarOption) => void;
}

export const EmailTemplateRichTextEditor: React.FC<EmailTemplateRichTextEditorProps> = ({
  editorClassName,
  errorMessage,
  headerLabel,
  placeholder = 'Enter email body...',
  readOnly = false,
  showEmailTemplateOptions = false,
  emailTemplatesEventTypesAllowed = [],
  value = '',
  onChange,
  onTemplateSelect,
}) => {
  const editor = useEmailTemplateEditor({ value, onChange, placeholder, editorClassName });

  const showAllEmailTemplatesEventTypes = useFeatureFlag([FeatureFlags.autoReminders])?.[FeatureFlags.autoReminders];

  const { data: allEmailTemplates = [], isPending } = useQuery({
    ...emailTemplatesQueries.getEmailTemplatesList(showAllEmailTemplatesEventTypes),
    enabled: !readOnly && showEmailTemplateOptions && showAllEmailTemplatesEventTypes !== undefined,
  });

  const emailTemplateToolbarOptions = useMemo(
    () => (showEmailTemplateOptions ? toEmailTemplateToolbarOptions(allEmailTemplates, emailTemplatesEventTypesAllowed) : []),
    [allEmailTemplates, showEmailTemplateOptions, emailTemplatesEventTypesAllowed]
  );

  const { Root, Header, ReadOnlyHtml, EditorRoot, ToolbarStrip, Body, ErrorCaption } = EmailTemplateRichTextEditorLayout;

  return (
    <Root>
      {headerLabel != null && headerLabel !== '' && <Header hasError={Boolean(errorMessage?.trim())} headerLabel={headerLabel} />}
      {readOnly ? (
        <ReadOnlyHtml html={value} />
      ) : (
        <>
          <EditorRoot>
            <DefaultFontLoader defaultFonts={DOWNLOADED_FONTS} />
            <ToolbarStrip>
              <FormattingToolbar
                editor={editor}
                showEmailTemplateOptions={showEmailTemplateOptions}
                emailTemplateOptions={emailTemplateToolbarOptions}
                isLoadingEmailTemplates={isPending}
                onEmailTemplateSelect={(option) => {
                  onChange(option.html);
                  onTemplateSelect?.(option);
                }}
              />
            </ToolbarStrip>
            <Body>
              <EditorContent editor={editor} />
            </Body>
          </EditorRoot>
          <ErrorCaption message={errorMessage} />
        </>
      )}
    </Root>
  );
};
